import { memoize } from "@use/decorators";

import {
  getStandardPageCount,
  getTextContent,
} from "@courses-pm2/api/src/ts/utils";

import { ContentElementData } from "@courses-pm2/api/src/ts/models/elements/ContentElementModel";
import { parseSimpleHtml } from "@courses-pm2/api/src/ts/parser/parseSimpleHtml";
import { CaptionedContentElementModel } from "./CaptionedContentElementModel";

export type SliderComponentDisplayTypes = "default" | "full-width" | "carousel";
export type SliderComponentBulletTypes = "dots" | "numbered" | "thumb";

/**
 * Specification of data needed to instantiate a new AccordionModel.
 */
type SliderProps =
  | "bulletType"
  | "displayCaption"
  | "displayType"
  | "carouselCount"
  | "slides";

export type SliderData = {
  [K in SliderProps]: SliderModel[K];
};

/**
 * Specification of the data associated with a single slide.
 */
export type Slide = {
  caption: {
    authorOrArtist?: string;
    copyright?: string;
    year?: string;
    description?: string;
    title?: string;
  };

  /**
   * Optionally specifies an absolute url to a remote page that should be
   * opened in a new window on click.
   */
  linkUrl?: string;

  /**
   * Optionally contains a link text that should be inserted into the button
   * that's added to the ui. If nothing is provided, clicking anywhere on the
   * slide will open the defined link.
   */
  linkText?: string;

  /**
   * Optionally specifies a custom color code to apply to the text in the
   * button.
   */
  linkTextColor?: string;

  /**
   * Optionally specifies a custom color code to apply to the background of
   * the button.
   */
  linkBackgroundColor?: string;

  /**
   * Specifies the absolute url to the image that should be displayed within
   * this slide.
   */
  src: string;

  height: number;
  width: number;
};

/**
 * Describes the content to be rendered for the element, so that it's made
 * easily available.
 */
export class SliderModel extends CaptionedContentElementModel {
  /**
   * Specifies what type of bullets to render below the slider, indicating the
   * amount of available slides and the currently active one.
   */
  public readonly bulletType: SliderComponentBulletTypes;

  public readonly displayType: SliderComponentDisplayTypes;

  /**
   * in case displayType is set to "carousel", then this number specifies the
   * desired amount of items available on a single page of the carousel,
   * given full width
   */
  public readonly carouselCount?: number;

  /**
   * Specifies if captions should be displayed on the slides within this
   * slider.
   */
  public readonly displayCaption?: boolean;

  /**
   * Contains the list of slides that should be contained in the slider.
   */
  public readonly slides: Slide[];

  /**
   * Apply data given when a new model is being instantiated.
   */
  constructor(initial: SliderData & ContentElementData) {
    super(initial);

    this.bulletType = initial.bulletType;
    this.displayCaption = initial.displayCaption;
    this.displayType = initial.displayType;
    this.carouselCount = initial.carouselCount;
    this.slides = initial.slides;
  }

  /**
   * Helper that'll render the title of the slider, adding support for certain
   * simple html tags.
   */
  @memoize()
  public renderTitle(): React.ReactNode {
    return this.title ? parseSimpleHtml(this.title) : undefined;
  }

  /**
   * If a preview image was supplied, then use that one directly - otherwise
   * fallback to a screengrab of the embedded page.
   */
  @memoize()
  public async getPreviewImageUrl(): Promise<string | undefined> {
    // Return the image of the first slide
    for (const slide of this.slides) {
      if (slide.src) {
        return slide.src;
      }
    }

    // If we get here, no preview image is available
    return undefined;
  }

  /**
   * Preview text is not available for a slider.
   */
  public getPreviewText(): undefined {
    return undefined;
  }

  /**
   * @inheritdoc
   *
   * Due to the nature of the calculation of standard pages, media elements
   * aren't counted on their own. We do however include the caption of the
   * element in the calculations.
   */
  // tslint:disable-next-line: completed-docs
  @memoize()
  public getStandardPageCount(): number {
    const textContent = [
      this.title || "",
      ...this.slides.map(
        (slide) => `${slide.caption || ""} ${slide.linkText || ""}`,
      ),
    ].join(" ");

    return getStandardPageCount(getTextContent(textContent));
  }
}
